class CartDrawer extends HTMLElement {
    
    constructor() {
        super();

        this.addEventListener(
            "keyup",
            (evt) => evt.code === "Escape" && this.close()
        );
        this.querySelector("#CartDrawer-Overlay").addEventListener(
            "click",
            this.close.bind(this)
        );
        this.setHeaderCartIconAccessibility();
        if( this.classList.contains("is-empty") ) this.displayRandomQuote();

        this.freeShippingBar = this.querySelector("#free-shipping-bar");
        this.normalizeDrawerItems("constructor");
        // Removes cart drawer "is-empty" class when Rebuy adds items
        document.addEventListener('rebuy.add', (event) => {
            // console.log('REBUY EVENT ADD', event.detail);
            if (this.classList.contains('is-empty')) {
                this.classList.remove('is-empty');
            }
        });
        this.handleGiftNoteAdded = this.handleGiftNoteAdded.bind(this);
        document.addEventListener('gift-note:added', this.handleGiftNoteAdded);
        window.WestonGiftNoteCartSync?.flush?.();
    }

    disconnectedCallback() {
        document.removeEventListener('gift-note:added', this.handleGiftNoteAdded);
        clearTimeout(this.rebuyInitTimer);
    }

    setHeaderCartIconAccessibility() {
        const cartLink = document.querySelector("#cart-icon-bubble");
        if (!cartLink) return;

        cartLink.setAttribute("role", "button");
        cartLink.setAttribute("aria-haspopup", "dialog");
        cartLink.addEventListener("click", (event) => {
            event.preventDefault();
            this.open(cartLink);
        });
        cartLink.addEventListener("keydown", (event) => {
            if (event.code.toUpperCase() === "SPACE") {
                event.preventDefault();
                this.open(cartLink);
            }
        });
    }

    open(triggeredBy) {
        window.cartOpened = true;
        if (triggeredBy) this.setActiveElement(triggeredBy);

        this.ensureRebuyWidgets();

        this.querySelector('.cart-drawer__overlay').classList.remove("o-0");
        this.classList.add("active");

        this.addEventListener(
            "transitionend",
            () => {
                const containerToTrapFocusOn = this.classList.contains("is-empty")
                    ? this.querySelector(".drawer__inner-empty")
                    : document.getElementById("cart-modal");
                const focusElement =
                    this.querySelector(".drawer__inner") ||
                    this.querySelector(".drawer__close");
                trapFocus(containerToTrapFocusOn, focusElement);
            },
            { once: true }
        );
        document.body.classList.add("overflow-hidden");
        this.normalizeDrawerItems("open");
        this.updateFreeShippingBar(this.calculateTotalPriceExclusion());
        // hide aaccesibility and chat elements
        document.querySelectorAll('#userwayAccessibilityIcon, #gorgias-chat-container').forEach(el => el.style.display = 'none');
    }

    ensureRebuyWidgets() {
        let insertedWidget = false;

        this.querySelectorAll('[data-rebuy-widget-id]').forEach((slot) => {
            if (slot.querySelector('[data-rebuy-id]')) return;

            const widgetId = slot.dataset.rebuyWidgetId;
            if (!widgetId) return;

            const wrapper = document.createElement('div');
            wrapper.className = slot.dataset.rebuyWidgetClass || 'rebuy-widget-wrapper';

            const placement = document.createElement('div');
            placement.dataset.rebuyId = widgetId;
            wrapper.appendChild(placement);
            slot.appendChild(wrapper);
            insertedWidget = true;
        });

        if (insertedWidget) this.initializeRebuyWidgets();
    }

    initializeRebuyWidgets(attempt = 0) {
        clearTimeout(this.rebuyInitTimer);

        if (typeof window.Rebuy?.init === 'function') {
            window.Rebuy.init();
            return;
        }

        if (attempt >= 20) return;

        this.rebuyInitTimer = setTimeout(() => {
            this.initializeRebuyWidgets(attempt + 1);
        }, 250);
    }

    close() {
        this.querySelector('.cart-drawer__overlay').classList.add("o-0");
        this.classList.remove("active");
        removeTrapFocus(this.activeElement);
        document.body.classList.remove("overflow-hidden");
        // console.log('Cart drawer closed');
        // show accessibility and chat elements
        document.querySelectorAll('#userwayAccessibilityIcon, #gorgias-chat-container').forEach(el => el.style.display = '');
    }

    updateFreeShippingBar(totalPrice) {
        if (!this.freeShippingBar) return;
        const qualifiedTotalPrice = Number.isFinite(Number(totalPrice)) ? Number(totalPrice) : 0;
        const freeShippingThreshold = this.freeShippingBar.dataset.freeShippingThreshold * 100; 
        const remainingAmount = freeShippingThreshold - qualifiedTotalPrice;
        const progressBar = this.querySelector(".progress-bar");
        const remainingAmountElement = this.querySelector(".remaining-amount");
        const freeShippingMessageContainer = this.freeShippingBar.querySelector(".free-shipping-message");
        const freeShippingAchievedMessage = this.freeShippingBar.dataset.freeShippingMessage; 
        const cartTotalPriceExclusion = this.querySelector('.cart-total-price-exclusion');

        if (!progressBar || !freeShippingMessageContainer) return;

        const progress = freeShippingThreshold > 0
            ? Math.max(0, Math.min(100, (qualifiedTotalPrice / freeShippingThreshold) * 100))
            : 0;

        if (remainingAmount > 0) {
            if (remainingAmountElement) {
                remainingAmountElement.textContent = `$${Math.ceil(remainingAmount / 100)}`;
            }
            progressBar.style.width = `${progress}%`;
            if (cartTotalPriceExclusion) {
                cartTotalPriceExclusion.style.color = '';
            }
        } else {
            freeShippingAchievedMessage ? freeShippingMessageContainer.textContent = freeShippingAchievedMessage : freeShippingMessageContainer.textContent = "Congratulations! You've got free shipping."
            progressBar.style.width = "100%";
            if (cartTotalPriceExclusion) {
                cartTotalPriceExclusion.style.color = 'green';
            }
        }

        if (cartTotalPriceExclusion) {
            cartTotalPriceExclusion.textContent = `$${(qualifiedTotalPrice / 100).toFixed(2)}`;
        }
        
    }

    calculateTotalPriceExclusion() {
        return window.WestonCartUI
            ? window.WestonCartUI.calculateQualifiedSubtotal(this, { scope: 'drawer' })
            : 0;
    }

    normalizeDrawerItems(reason) {
        const root = this.querySelector('cart-drawer-items') || this;

        window.WestonCartUI?.normalizeLineItems(root, {
            scope: 'drawer',
            reason,
        });
    }

    setSummaryAccessibility(cartDrawerNote) {
        cartDrawerNote.setAttribute("role", "button");
        cartDrawerNote.setAttribute("aria-expanded", "false");

        if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
            cartDrawerNote.setAttribute(
                "aria-controls",
                cartDrawerNote.nextElementSibling.id
            );
        }

        cartDrawerNote.addEventListener("click", (event) => {
            event.currentTarget.setAttribute(
                "aria-expanded",
                !event.currentTarget.closest("details").hasAttribute("open")
            );
        });

        cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
    }

    renderContents(parsedState) {
        this.querySelector(".drawer__inner").classList.contains("is-empty") &&
            this.querySelector(".drawer__inner").classList.remove("is-empty");
        this.productId = parsedState.id;
        this.getSectionsToRender().forEach((section) => {
            const sectionKey = section.section || section.id;
            const sectionElement = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);
            const sectionInnerHTML = this.getSectionInnerHTML(
                parsedState.sections?.[sectionKey] ?? parsedState.sections?.[section.id],
                section.selector
            );

            if (!sectionElement || sectionInnerHTML === null) {
                return;
            }

            this.replaceSectionHTML(sectionElement, sectionInnerHTML, section);
        });

        setTimeout(() => {
            this.querySelector("#CartDrawer-Overlay").addEventListener(
                "click",
                this.close.bind(this)
            );
            this.freeShippingBar = this.querySelector("#free-shipping-bar");
            this.normalizeDrawerItems("renderContents");
            this.open();
            this.updateFreeShippingBar(this.calculateTotalPriceExclusion());
            
            if (typeof yotpoWidgetsContainer !== 'undefined') {
                yotpoWidgetsContainer.initWidgets();
            }
        });
    }

    handleGiftNoteAdded(event) {
        const detail = event && event.detail ? event.detail : {};
        if (detail.source === 'weston-table-cart-drawer') return;

        this.renderGiftNoteCartContents(detail.cart || detail.cartData || null);
    }

    renderGiftNoteCartContents(cart) {
        const currentUrl = new URL(`${window.location.pathname}${window.location.search}`, window.location.origin);
        const sectionRequests = this.getSectionsToRender().map((section) => {
            const sectionKey = section.section || section.id;
            const sectionUrl = new URL(currentUrl.toString());

            sectionUrl.searchParams.set('section_id', sectionKey);

            return fetch(sectionUrl.toString(), {
                credentials: 'same-origin',
                headers: {
                    Accept: 'text/html',
                },
            })
                .then((response) => response.text())
                .then((html) => ({ key: sectionKey, html }));
        });

        Promise.all(sectionRequests)
            .then((results) => {
                const sections = results.reduce((accumulator, result) => {
                    accumulator[result.key] = result.html;
                    return accumulator;
                }, {});

                this.classList.toggle('is-empty', cart && cart.item_count === 0);
                this.renderContents({
                    id: cart && cart.items && cart.items[0] ? cart.items[0].id : null,
                    sections,
                });
                this.dispatchGiftNoteDrawerRendered(cart);
            })
            .catch((error) => {
                console.error('Error refreshing cart drawer after gift note update:', error);
                this.open(document.getElementById('cart-icon-bubble'));
                this.dispatchGiftNoteDrawerRendered(cart);
            });
    }

    dispatchGiftNoteDrawerRendered(cart) {
        const detail = {
            source: 'weston-table-cart-drawer',
            cart,
            cartData: cart,
            itemCount: cart && typeof cart.item_count === 'number' ? cart.item_count : 0,
        };

        [80, 250, 600].forEach((delay) => {
            window.setTimeout(() => {
                document.dispatchEvent(
                    new CustomEvent('gift-note:added', {
                        bubbles: true,
                        detail,
                    })
                );
                window.dispatchEvent(
                    new CustomEvent('gift-note:added', {
                        detail,
                    })
                );
                document.dispatchEvent(
                    new CustomEvent('cart:updated', {
                        bubbles: true,
                        detail,
                    })
                );
            }, delay);
        });
    }

    getSectionInnerHTML(html, selector) {
        if (typeof html !== "string" || html.trim() === "") {
            return null;
        }

        const parsedHtml = new DOMParser().parseFromString(html, "text/html");
        const sectionElement = selector ? parsedHtml.querySelector(selector) : null;

        if (sectionElement) {
            return sectionElement.innerHTML;
        }

        if (!selector || selector === ".shopify-section") {
            const bodyMarkup = parsedHtml.body ? parsedHtml.body.innerHTML.trim() : "";
            return bodyMarkup || null;
        }

        return null;
    }

    replaceSectionHTML(sectionElement, sectionInnerHTML, section) {
        if (section.id !== "cart-modal") {
            sectionElement.innerHTML = sectionInnerHTML;
            return;
        }

        const preservedRebuyWidgets = Array.from(sectionElement.querySelectorAll(".rebuy-widget-wrapper")).map((widget) => {
            const widgetId = widget.querySelector("[data-rebuy-id]")?.dataset.rebuyId || "";
            return { widget, widgetId };
        });

        sectionElement.innerHTML = sectionInnerHTML;

        preservedRebuyWidgets.forEach(({ widget, widgetId }) => {
            const replacementTarget = widgetId
                ? sectionElement.querySelector(`[data-rebuy-widget-id="${widgetId}"]`)
                : null;

            if (replacementTarget && widget.isConnected === false) {
                replacementTarget.appendChild(widget);
            }
        });
    }

    getSectionsToRender() {
        return [
            {
                id: "cart-modal",
                section: "cart-modal",
                selector: ".drawer__inner",
            },
            {
                id: "cart-icon-bubble",
                section: "cart-icon-bubble",
            },
        ];
    }

    getSectionDOM(html, selector = ".shopify-section") {
        return new DOMParser()
            .parseFromString(html, "text/html")
            .querySelector(selector);
    }

    setActiveElement(element) {
        this.activeElement = element;
    }
    displayRandomQuote(){
        const quotes = this.getElementsByClassName("cart_quote");
        const size = quotes.length;
        if( size > 0 ){
            var random = Math.floor(Math.random() * size);
            quotes[random].classList.remove("dn")
        }
    }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
    constructor() {
        super();
        // Initialize event listeners only once
        CartDrawerItems.initializeRebuyListeners();
    }

    static initializeRebuyListeners() {
        // Only add event listeners once globally to prevent multiple executions
        if (CartDrawerItems.rebuyListenersInitialized) return;
        
        CartDrawerItems.rebuyListenersInitialized = true;
        CartDrawerItems.refreshTimeout = null;
        
        const throttledRefresh = () => {
            // Clear any existing timeout to prevent multiple rapid calls
            if (CartDrawerItems.refreshTimeout) {
                clearTimeout(CartDrawerItems.refreshTimeout);
            }
            
            // Set a small delay to batch multiple rapid events
            CartDrawerItems.refreshTimeout = setTimeout(() => {
                CartDrawerItems.refreshCartDrawerItems();
            }, 150);
        };
        
        document.addEventListener("rebuy:cart.add", () => {
            // console.log("Rebuy cart updated");
            throttledRefresh();
        });
        
        // console.log("Rebuy cart listeners initialized once");
    }

    static refreshCartDrawerItems() {
        // Get the cart drawer element
        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer) return;

        // Fetch updated cart data
        fetch(window.Shopify.routes.root + 'cart.js')
            .then(response => response.json())
            .then(cart => {
                // Update cart count bubble
                const cartCountBubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
                const cartCount = cartCountBubble || document.querySelector('#cart-icon-bubble .cart-count');
                const cartCountText = document.querySelector('#cart-icon-bubble .cart-count-text');
                const itemCount = cart.item_count;

                if (cartCountText) {
                    cartCountText.textContent = `, ${itemCount} item${itemCount === 1 ? '' : 's'}`;
                }

                if (cartCountBubble) {
                    cartCountBubble.textContent = itemCount;
                    cartCountBubble.style.display = itemCount > 0 ? 'flex' : 'none';
                } else if (cartCount) {
                    cartCount.textContent = `(${itemCount})`;
                    cartCount.style.display = itemCount > 0 ? 'block' : 'none';
                }

                // Fetch updated cart drawer HTML
                const sectionUrl = new URL(`${window.location.pathname}${window.location.search}`, window.location.origin);
                sectionUrl.searchParams.set('section_id', 'cart-modal');
                return fetch(sectionUrl.toString());
            })
            .then(response => response.text())
            .then(html => {
                // Parse the HTML and update the cart drawer
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = html;
                const newDrawerInner = tempDiv.querySelector('.drawer__inner');
                
                if (newDrawerInner) {
                    const currentDrawerInner = cartDrawer.querySelector('.drawer__inner');
                    if (currentDrawerInner) {
                        cartDrawer.replaceSectionHTML(
                            currentDrawerInner,
                            newDrawerInner.innerHTML,
                            { id: 'cart-modal' }
                        );
                        cartDrawer.freeShippingBar = cartDrawer.querySelector("#free-shipping-bar");
                        cartDrawer.normalizeDrawerItems('rebuy-refresh');
                        cartDrawer.ensureRebuyWidgets();
                        
                        // Update free shipping bar if cart drawer is open
                        if (cartDrawer.classList.contains('active')) {
                            const freeShippingBar = cartDrawer.querySelector("#free-shipping-bar");
                            if (freeShippingBar && cartDrawer.updateFreeShippingBar) {
                                cartDrawer.updateFreeShippingBar(cartDrawer.calculateTotalPriceExclusion());
                            }
                        }
                        
                        // Re-attach overlay click listener
                        const overlay = cartDrawer.querySelector("#CartDrawer-Overlay");
                        if (overlay) {
                            overlay.replaceWith(overlay.cloneNode(true));
                            cartDrawer.querySelector("#CartDrawer-Overlay").addEventListener(
                                "click",
                                cartDrawer.close.bind(cartDrawer)
                            );
                        }
                        
                        // console.log("Cart drawer items refreshed successfully");
                    }
                }
            })
            .catch(error => {
                console.error('Error refreshing cart drawer items:', error);
            });
    }

    getSectionsToRender() {
        return [
            {
                id: "cart-modal",
                section: "cart-modal",
                selector: ".drawer__inner",
            },
            {
                id: "cart-icon-bubble",
                section: "cart-icon-bubble",
                selector: ".shopify-section",
            },
        ];
    }  

    renderContents(parsedState) {
        super.renderContents(parsedState);
    }

    calculateTotalPriceExclusion() {
        return window.WestonCartUI
            ? window.WestonCartUI.calculateQualifiedSubtotal(this, { scope: "drawer" })
            : 0;
    }
}

customElements.define("cart-drawer-items", CartDrawerItems);
